import { Spin, Typography } from 'antd'
import { useCdfRawValues2DArray, useColorScale } from 'features/dataLayers'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import Plot from 'features/plotly'
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurement,
  PipeOrientation,
} from 'types'

const { Text } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`

const HeatmapContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  overflow: hidden;
  cursor: crosshair;
`

const SelectedValueDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.25rem;
  min-height: 1.5rem;
`

// Fallback color scale if we can't get one from the context
const FALLBACK_COLOR_SCALE: [number, string][] = [
  [0, '#d32f2f'],
  [0.25, '#ff9800'],
  [0.5, '#ffeb3b'],
  [0.75, '#8bc34a'],
  [1, '#4caf50'],
]

export interface SelectedBinValue {
  binX: number
  binY: number
  measurement: GeckoMeasurement
}

export interface InspectionHeatmapPickerProps {
  cdf: GeckoComponentDataFrame | undefined
  region: GeckoComponentRect | undefined
  selectedBin: SelectedBinValue | null
  onBinSelect: (bin: SelectedBinValue | null) => void
  title: string
  width?: number
  height?: number
  /** Original indication region (for boundary selection mode) */
  originalRegion?: GeckoComponentRect
  /** Enable boundary selection mode (only perimeter) */
  boundarySelectionMode?: boolean
}

/**
 * A heatmap component that displays thickness data from a CDF and allows
 * the user to click on a bin to select its value.
 */
export const InspectionHeatmapPicker: FC<InspectionHeatmapPickerProps> = ({
  cdf,
  region,
  selectedBin,
  onBinSelect,
  title,
  width = 250,
  height = 250,
  originalRegion,
  boundarySelectionMode = false,
}) => {
  const { colorScaleThreshold, assetModel, component } = useIndicationData()

  // For horizontal pipes, swap axes so length goes left-right and circumference goes up-down
  const shouldSwapAxes = useMemo(() => {
    if (!assetModel || !component) return false
    const componentModel = assetModel.data.components.get(component.id)
    if (!componentModel) return false

    if ('orientation' in componentModel) {
      return componentModel.orientation === PipeOrientation.Horizontal
    }
    return false
  }, [assetModel, component])

  const { colorScale, colorScaleMin, colorScaleMax } = useColorScale(
    cdf,
    colorScaleThreshold
  )

  // Convert GeckoColorScale to Plotly colorscale format
  const plotlyColorScale = useMemo((): [number, string][] => {
    if (
      !colorScale ||
      typeof colorScale.getColorForValue !== 'function' ||
      colorScaleMin === undefined ||
      colorScaleMax === undefined
    ) {
      return FALLBACK_COLOR_SCALE
    }

    const numPoints = 10
    const range = colorScaleMax - colorScaleMin
    if (range === 0) return FALLBACK_COLOR_SCALE

    const scale: [number, string][] = []
    for (let i = 0; i <= numPoints; i++) {
      const normalizedValue = i / numPoints
      const actualValue = colorScaleMin + normalizedValue * range
      const color = colorScale.getColorForValue(actualValue)
      scale.push([normalizedValue, color])
    }
    return scale
  }, [colorScale, colorScaleMin, colorScaleMax])

  const { array: data, isLoading } = useCdfRawValues2DArray(cdf, region)

  // Calculate min/max from data for fallback color scale
  const { dataMin, dataMax } = useMemo(() => {
    if (!data || data.length === 0) {
      return { dataMin: undefined, dataMax: undefined }
    }

    let min = Infinity
    let max = -Infinity

    for (const row of data) {
      for (const value of row) {
        if (!isNaN(value) && isFinite(value)) {
          if (value < min) min = value
          if (value > max) max = value
        }
      }
    }

    return {
      dataMin: min === Infinity ? undefined : min,
      dataMax: max === -Infinity ? undefined : max,
    }
  }, [data])

  const { xPositions, yPositions } = useMemo(() => {
    if (!region || !data || data.length === 0) {
      return { xPositions: [], yPositions: [] }
    }

    const numRows = data.length
    const numCols = data[0]?.length ?? 0

    const regionXStart = region.start.x.num
    const regionXEnd = region.end.x.num
    const regionYStart = region.start.y.num
    const regionYEnd = region.end.y.num

    const xPos: number[] = []
    for (let i = 0; i < numCols; i++) {
      const pos =
        regionXStart + (i / (numCols - 1 || 1)) * (regionXEnd - regionXStart)
      xPos.push(pos)
    }

    const yPos: number[] = []
    for (let i = 0; i < numRows; i++) {
      const pos =
        regionYStart + (i / (numRows - 1 || 1)) * (regionYEnd - regionYStart)
      yPos.push(pos)
    }

    if (shouldSwapAxes) {
      return {
        xPositions: yPos,
        yPositions: xPos,
      }
    }

    return { xPositions: xPos, yPositions: yPos }
  }, [region, data, shouldSwapAxes])

  const isInOriginalRegion = useCallback(
    (posX: number, posY: number): boolean => {
      if (!boundarySelectionMode || !originalRegion) return false

      const xInRange =
        posX >= originalRegion.start.x.num && posX <= originalRegion.end.x.num
      const yInRange =
        posY >= originalRegion.start.y.num && posY <= originalRegion.end.y.num

      return xInRange && yInRange
    },
    [boundarySelectionMode, originalRegion]
  )

  const handleClick = useCallback(
    (event: Plotly.PlotMouseEvent) => {
      if (
        !event.points ||
        event.points.length === 0 ||
        !cdf ||
        !data ||
        !region
      )
        return

      const point = event.points[0]
      const clickX = typeof point.x === 'number' ? point.x : Number(point.x)
      const clickY = typeof point.y === 'number' ? point.y : Number(point.y)
      const value = 'z' in point && typeof point.z === 'number' ? point.z : NaN

      if (isNaN(value) || isNaN(clickX) || isNaN(clickY)) {
        onBinSelect(null)
        return
      }

      const numRows = data.length
      const numCols = data[0]?.length ?? 0
      const regionXStart = region.start.x.num
      const regionXEnd = region.end.x.num
      const regionYStart = region.start.y.num
      const regionYEnd = region.end.y.num

      // If axes are swapped, clickX/Y correspond to swapped positions
      const posX = shouldSwapAxes ? clickY : clickX
      const posY = shouldSwapAxes ? clickX : clickY

      // In boundary selection mode, reject clicks inside the original region
      if (isInOriginalRegion(posX, posY)) {
        onBinSelect(null)
        return
      }

      const binX = Math.round(
        ((posX - regionXStart) / (regionXEnd - regionXStart || 1)) *
          (numCols - 1)
      )
      const binY = Math.round(
        ((posY - regionYStart) / (regionYEnd - regionYStart || 1)) *
          (numRows - 1)
      )

      onBinSelect({
        binX,
        binY,
        measurement: GeckoMeasurement.CreateFromValue(
          value,
          cdf.data.params.measurement_unit
        ),
      })
    },
    [cdf, data, region, onBinSelect, shouldSwapAxes, isInOriginalRegion]
  )

  const selectedMarker = useMemo(() => {
    if (!selectedBin || !data || !region) return null

    const numRows = data.length
    const numCols = data[0]?.length ?? 0
    const regionXStart = region.start.x.num
    const regionXEnd = region.end.x.num
    const regionYStart = region.start.y.num
    const regionYEnd = region.end.y.num

    const posX =
      regionXStart +
      (selectedBin.binX / (numCols - 1 || 1)) * (regionXEnd - regionXStart)
    const posY =
      regionYStart +
      (selectedBin.binY / (numRows - 1 || 1)) * (regionYEnd - regionYStart)

    const markerX = shouldSwapAxes ? posY : posX
    const markerY = shouldSwapAxes ? posX : posY

    return {
      x: [markerX],
      y: [markerY],
      mode: 'markers' as const,
      type: 'scatter' as const,
      marker: {
        size: 12,
        color: 'white',
        symbol: 'circle-open',
        line: { width: 3, color: 'white' },
      },
      hoverinfo: 'skip' as const,
    }
  }, [selectedBin, data, region, shouldSwapAxes])

  const unitName = useMemo(() => {
    return cdf?.data.params.measurement_unit?.name ?? 'mm'
  }, [cdf])

  // Transpose data if axes are swapped
  const mutableData = useMemo(() => {
    if (!data) return undefined

    if (shouldSwapAxes) {
      const rows = data.length
      const cols = data[0]?.length ?? 0
      const transposed: number[][] = []
      for (let c = 0; c < cols; c++) {
        const newRow: number[] = []
        for (let r = 0; r < rows; r++) {
          newRow.push(data[r][c])
        }
        transposed.push(newRow)
      }
      return transposed
    }

    return data.map((row) => [...row])
  }, [data, shouldSwapAxes])

  const originalRegionOverlay = useMemo(() => {
    if (!boundarySelectionMode || !originalRegion) return null

    const x0 = originalRegion.start.x.num
    const y0 = originalRegion.start.y.num
    const x1 = originalRegion.end.x.num
    const y1 = originalRegion.end.y.num

    const rectX0 = shouldSwapAxes ? y0 : x0
    const rectY0 = shouldSwapAxes ? x0 : y0
    const rectX1 = shouldSwapAxes ? y1 : x1
    const rectY1 = shouldSwapAxes ? x1 : y1

    return {
      type: 'rect' as const,
      x0: rectX0,
      y0: rectY0,
      x1: rectX1,
      y1: rectY1,
      fillcolor: 'rgba(128, 128, 128, 0.6)',
      line: {
        color: 'rgba(255, 255, 255, 0.8)',
        width: 2,
      },
      layer: 'above' as const,
    }
  }, [boundarySelectionMode, originalRegion, shouldSwapAxes])

  const plotData = useMemo((): Plotly.Data[] => {
    const traces: Plotly.Data[] = []

    if (mutableData && xPositions.length > 0 && yPositions.length > 0) {
      const heatmapTrace: Plotly.Data = {
        z: mutableData,
        x: xPositions,
        y: yPositions,
        type: 'heatmap',
        colorscale: plotlyColorScale,
        showscale: true,
        reversescale: false,
        hovertemplate: `X: %{x:.2f}<br>Y: %{y:.2f}<br>Thickness: %{z:.3f} ${unitName}<extra></extra>`,
        colorbar: {
          thickness: 15,
          len: 0.8,
          title: {
            text: unitName,
          },
        },
      }

      // Only set zmin/zmax for fallback color scale (data-driven)
      // For proper GeckoColorScale, let it handle its own range
      if (
        !colorScale ||
        typeof colorScale.getColorForValue !== 'function' ||
        colorScaleMin === undefined ||
        colorScaleMax === undefined
      ) {
        if (dataMin !== undefined && dataMax !== undefined) {
          heatmapTrace.zmin = dataMin
          heatmapTrace.zmax = dataMax
        }
      }

      traces.push(heatmapTrace)
    }

    if (selectedMarker) traces.push(selectedMarker)

    return traces
  }, [
    mutableData,
    xPositions,
    yPositions,
    selectedMarker,
    unitName,
    plotlyColorScale,
    colorScale,
    colorScaleMin,
    colorScaleMax,
    dataMin,
    dataMax,
  ])

  const formattedValue = useMemo(() => {
    if (!selectedBin) return 'Click to select'
    return selectedBin.measurement.unit.formatNumber({
      num: selectedBin.measurement.num,
      locale: 'en-US',
      variant: 'geometric',
    })
  }, [selectedBin])

  if (!cdf) {
    return (
      <Container>
        <Text strong>{title}</Text>
        <Text type='secondary'>No CDF available</Text>
      </Container>
    )
  }

  return (
    <Container>
      <Text strong>{title}</Text>
      <Spin spinning={isLoading}>
        <HeatmapContainer>
          <Plot
            data={plotData}
            layout={{
              width,
              height,
              margin: { t: 10, b: 35, l: 45, r: 60 },
              xaxis: {
                showticklabels: true,
                ticks: 'outside',
                tickfont: { size: 9, color: '#999' },
                title: {
                  text: shouldSwapAxes
                    ? region?.y.unit.name
                    : region?.x.unit.name,
                  font: { size: 9, color: '#999' },
                },
              },
              yaxis: {
                showticklabels: true,
                ticks: 'outside',
                tickfont: { size: 9, color: '#999' },
                title: {
                  text: shouldSwapAxes
                    ? region?.x.unit.name
                    : region?.y.unit.name,
                  font: { size: 9, color: '#999' },
                },
              },
              plot_bgcolor: 'rgba(0, 0, 0, 0)',
              paper_bgcolor: 'rgba(0, 0, 0, 0)',
              shapes: originalRegionOverlay ? [originalRegionOverlay] : [],
            }}
            config={{
              displayModeBar: false,
              responsive: true,
            }}
            onClick={handleClick}
          />
        </HeatmapContainer>
      </Spin>
      <SelectedValueDisplay>
        <Text type='secondary'>Selected:</Text>
        <Text strong style={{ color: selectedBin ? '#4caf50' : '#999' }}>
          {formattedValue}
        </Text>
      </SelectedValueDisplay>
    </Container>
  )
}
